import React, { useState, useEffect } from 'react';
import { 
    Plus, User, Phone, Calendar, Check, Trash2, 
    MessageCircle, Loader2, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MobileLayout from '../components/layout/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';

const Khata = () => {
    const { authAxios } = useAuth();
    const { t, language } = useLanguage();
    
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [showPaid, setShowPaid] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // New entry form
    const [newEntry, setNewEntry] = useState({
        customer_name: '',
        customer_phone: '',
        amount_due: '',
        due_date: '',
        notes: ''
    });
    const [saving, setSaving] = useState(false);

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const response = await authAxios.get(`/khata?show_paid=${showPaid}`);
            setEntries(response.data);
        } catch (err) {
            console.error('Fetch khata error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [showPaid]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const isOverdue = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    };

    const handleAddEntry = async () => {
        if (!newEntry.customer_name || !newEntry.amount_due) return;
        
        setSaving(true);
        try {
            await authAxios.post('/khata', {
                ...newEntry,
                amount_due: parseFloat(newEntry.amount_due)
            });
            setAddModalOpen(false);
            setNewEntry({
                customer_name: '',
                customer_phone: '',
                amount_due: '',
                due_date: '',
                notes: ''
            });
            fetchEntries();
        } catch (err) {
            console.error('Add entry error:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleMarkPaid = async (entryId) => {
        try {
            await authAxios.put(`/khata/${entryId}/paid`);
            fetchEntries();
        } catch (err) {
            console.error('Mark paid error:', err);
        }
    };

    const handleDelete = async (entryId) => {
        if (!window.confirm(language === 'en' ? 'Delete this entry?' : 'इस एंट्री को हटाएं?')) return;
        
        try {
            await authAxios.delete(`/khata/${entryId}`);
            fetchEntries();
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const sendWhatsAppReminder = (entry) => {
        if (!entry.customer_phone) {
            alert(language === 'en' ? 'No phone number available' : 'फोन नंबर उपलब्ध नहीं है');
            return;
        }
        
        const message = language === 'en'
            ? `Hello ${entry.customer_name}, this is a friendly reminder about your pending payment of ${formatCurrency(entry.amount_due)}. Please clear it at your earliest convenience. Thank you!`
            : `नमस्ते ${entry.customer_name}, यह ${formatCurrency(entry.amount_due)} की आपकी बकाया राशि की याद दिलाने के लिए है। कृपया जल्द से जल्द भुगतान करें। धन्यवाद!`;
        
        const url = `https://wa.me/${entry.customer_phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    const totalDue = entries.filter(e => !e.is_paid).reduce((sum, e) => sum + e.amount_due, 0);

    const filteredEntries = entries.filter(entry => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            entry.customer_name?.toLowerCase().includes(query) ||
            entry.customer_phone?.includes(query) ||
            entry.notes?.toLowerCase().includes(query)
        );
    });

    return (
        <MobileLayout>
            <div className="space-y-4" data-testid="khata-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold font-heading">{t('khata')}</h1>
                    <Button 
                        onClick={() => setAddModalOpen(true)}
                        data-testid="add-khata-btn"
                    >
                        <Plus className="w-5 h-5 mr-1" />
                        {t('addCredit')}
                    </Button>
                </div>

                {/* Total Due Card */}
                <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="total-due-card">
                    <CardContent className="p-4">
                        <p className="text-sm opacity-90">{t('totalDue')}</p>
                        <p className="text-3xl font-bold font-number">{formatCurrency(totalDue)}</p>
                        <p className="text-sm opacity-75 mt-1">
                            {entries.filter(e => !e.is_paid).length} {language === 'en' ? 'customers' : 'ग्राहक'}
                        </p>
                    </CardContent>
                </Card>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder={language === 'en' ? 'Search customers...' : 'ग्राहक खोजें...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="khata-search"
                    />
                </div>

                {/* Tabs */}
                <Tabs value={showPaid ? 'paid' : 'pending'} onValueChange={(v) => setShowPaid(v === 'paid')}>
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="pending" data-testid="tab-pending">
                            {language === 'en' ? 'Pending' : 'बकाया'}
                        </TabsTrigger>
                        <TabsTrigger value="paid" data-testid="tab-paid">
                            {language === 'en' ? 'Paid' : 'भुगतान हुआ'}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Entries List */}
                {loading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => (
                            <Skeleton key={i} className="h-24" />
                        ))}
                    </div>
                ) : filteredEntries.length > 0 ? (
                    <div className="space-y-2">
                        {filteredEntries.map((entry) => (
                            <Card 
                                key={entry.id}
                                className={cn(
                                    entry.is_paid ? "khata-paid" : "khata-due",
                                    isOverdue(entry.due_date) && !entry.is_paid && "bg-rose-50 dark:bg-rose-900/10"
                                )}
                                data-testid={`khata-entry-${entry.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 rounded-full bg-muted">
                                                <User className="w-5 h-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="font-medium">{entry.customer_name}</p>
                                                {entry.customer_phone && (
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {entry.customer_phone}
                                                    </p>
                                                )}
                                                {entry.due_date && (
                                                    <p className={cn(
                                                        "text-xs flex items-center gap-1 mt-1",
                                                        isOverdue(entry.due_date) && !entry.is_paid 
                                                            ? "text-rose-600" 
                                                            : "text-muted-foreground"
                                                    )}>
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(entry.due_date).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN')}
                                                        {isOverdue(entry.due_date) && !entry.is_paid && (
                                                            <Badge variant="destructive" className="text-xs ml-1">
                                                                {language === 'en' ? 'Overdue' : 'अतिदेय'}
                                                            </Badge>
                                                        )}
                                                    </p>
                                                )}
                                                {entry.notes && (
                                                    <p className="text-xs text-muted-foreground mt-1 italic">
                                                        {entry.notes}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={cn(
                                                "font-bold font-number text-lg",
                                                entry.is_paid ? "text-emerald-600" : "text-rose-600"
                                            )}>
                                                {formatCurrency(entry.amount_due)}
                                            </p>
                                            {entry.is_paid && (
                                                <Badge className="bg-emerald-500">
                                                    <Check className="w-3 h-3 mr-1" />
                                                    {language === 'en' ? 'Paid' : 'भुगतान'}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Actions */}
                                    {!entry.is_paid && (
                                        <div className="flex gap-2 mt-3 pt-3 border-t">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="flex-1"
                                                onClick={() => handleMarkPaid(entry.id)}
                                                data-testid={`mark-paid-${entry.id}`}
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                {t('markPaid')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                                                onClick={() => sendWhatsAppReminder(entry)}
                                                data-testid={`send-reminder-${entry.id}`}
                                            >
                                                <MessageCircle className="w-4 h-4 mr-1" />
                                                WhatsApp
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-destructive"
                                                onClick={() => handleDelete(entry.id)}
                                                data-testid={`delete-${entry.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{language === 'en' ? 'No entries found' : 'कोई एंट्री नहीं मिली'}</p>
                    </div>
                )}

                {/* Add Entry Modal */}
                <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
                    <DialogContent data-testid="add-khata-modal">
                        <DialogHeader>
                            <DialogTitle>{t('addCredit')}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            {/* Customer Name */}
                            <div>
                                <label className="text-sm font-medium">{t('customerName')} *</label>
                                <Input
                                    placeholder={language === 'en' ? 'Enter name...' : 'नाम दर्ज करें...'}
                                    value={newEntry.customer_name}
                                    onChange={(e) => setNewEntry(prev => ({ ...prev, customer_name: e.target.value }))}
                                    className="mt-1"
                                    data-testid="customer-name-input"
                                />
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="text-sm font-medium">
                                    {language === 'en' ? 'Phone Number' : 'फोन नंबर'}
                                </label>
                                <Input
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={newEntry.customer_phone}
                                    onChange={(e) => setNewEntry(prev => ({ ...prev, customer_phone: e.target.value }))}
                                    className="mt-1"
                                    data-testid="customer-phone-input"
                                />
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="text-sm font-medium">{t('amountDue')} *</label>
                                <div className="relative mt-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                    <Input
                                        type="number"
                                        placeholder="0"
                                        value={newEntry.amount_due}
                                        onChange={(e) => setNewEntry(prev => ({ ...prev, amount_due: e.target.value }))}
                                        className="pl-8 text-xl font-number"
                                        data-testid="amount-due-input"
                                    />
                                </div>
                            </div>

                            {/* Due Date */}
                            <div>
                                <label className="text-sm font-medium">{t('dueDate')}</label>
                                <Input
                                    type="date"
                                    value={newEntry.due_date}
                                    onChange={(e) => setNewEntry(prev => ({ ...prev, due_date: e.target.value }))}
                                    className="mt-1"
                                    data-testid="due-date-input"
                                />
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="text-sm font-medium">
                                    {language === 'en' ? 'Notes' : 'नोट्स'}
                                </label>
                                <Input
                                    placeholder={language === 'en' ? 'Optional notes...' : 'वैकल्पिक नोट्स...'}
                                    value={newEntry.notes}
                                    onChange={(e) => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                                    className="mt-1"
                                    data-testid="notes-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                                {t('cancel')}
                            </Button>
                            <Button 
                                onClick={handleAddEntry}
                                disabled={!newEntry.customer_name || !newEntry.amount_due || saving}
                                data-testid="save-khata-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                {t('save')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </MobileLayout>
    );
};

export default Khata;
